import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ComponentType, ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Button } from '../ui/Button';
import { Loader } from '../ui/Loader';
import { cn } from '@/utils/lib/cn';

type Options = {
  ErrorFallback?: React.ComponentType<{ resetErrorBoundary: () => void }>;
  SuspenseFallback?: ReactNode;
  error?: {
    className?: string;
  };
};

//eslint-disable-next-line react-refresh/only-export-components
export function withErrorAndSuspense<T extends object>(
  WrappedComponent: ComponentType<T>,
  options?: Options,
) {
  const {
    ErrorFallback = DefaultErrorFallback,
    SuspenseFallback = <DefaultLoader />,
    error,
  } = options || {};

  const ComponentWithBoundaries = (props: T) => {
    return (
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            onReset={reset}
            fallbackRender={({ resetErrorBoundary }) => (
              <ErrorFallback
                className={error?.className}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
          >
            <Suspense fallback={SuspenseFallback}>
              <WrappedComponent {...props} />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    );
  };

  ComponentWithBoundaries.displayName = `withErrorAndSuspense(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return ComponentWithBoundaries;
}

export function DefaultLoader() {
  return <Loader className='my-0' />;
}

export function DefaultErrorFallback({
  resetErrorBoundary,
  className,
}: {
  resetErrorBoundary: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid place-items-center space-y-2 text-red-500',
        className,
      )}
    >
      <p>Something went wrong.</p>
      <Button onClick={resetErrorBoundary}> Please Try again</Button>
    </div>
  );
}
